import "core-js/modules/es.object.assign.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { ElevationPropType, SchemePropType, ColorPropType } from '@anewgo/prop-types';
import ImageDisplay from '@anewgo/image-display';
import { usePrevious, useImageFetch, useResizeObserver } from '@anewgo/hooks';
import { computeExteriorUrl } from '@anewgo/utils';

var Alert = function Alert(props) {
  return /*#__PURE__*/React.createElement(MuiAlert, _extends({
    elevation: 6,
    variant: "filled"
  }, props));
};

var ExteriorImage = function ExteriorImage(props) {
  var mountedRef = useRef();

  var _useState = useState(null),
      exteriorImg = _useState[0],
      setExteriorImg = _useState[1];

  var _useState2 = useState(false),
      displayPaintedBrickInfoMsg = _useState2[0],
      setDisplayPaintedBrickMessage = _useState2[1];

  var flip = props.flip,
      apiKey = props.apiKey,
      toggleExteriorMenu = props.toggleExteriorMenu,
      colorMtd = props.colorMtd,
      selectedColors = props.colorSelections,
      elevation = props.elevation,
      directoryName = props.directoryName,
      width = props.width,
      height = props.height,
      engineUrl = props.engineUrl,
      client = props.client,
      community = props.community,
      plan = props.plan,
      selectedScheme = props.selectedScheme,
      style = props.style,
      doneCallback = props.doneCallback,
      startCallback = props.startCallback,
      maxWidth = props.maxWidth,
      descriptionPoints = props.descriptionPoints;
  var prevExteriorImg = usePrevious(exteriorImg);
  var parentRef = useRef(null);

  var _useResizeObserver = useResizeObserver(parentRef),
      parentWidth = _useResizeObserver[0]; //memoize the proper color selections to handle the painted brick scenario

  /* exception for a painted brick scenario, where we don't want colored bricks in eg.: masonry.
   Check for ID (table "palettes") of painted brick and delete colors layers,
   if selected other than painted brick
   */


  var colorSelections = useMemo(function () {
    var finalColors = Object.assign({}, selectedColors);

    if ((12 in finalColors || 18 in finalColors) && 246 in finalColors) {
      var brickSelectionValue = finalColors[12] || finalColors[18];

      if (brickSelectionValue && brickSelectionValue.name && !brickSelectionValue.name.toLowerCase().includes('painted brick')) {
        setDisplayPaintedBrickMessage(true);
        delete finalColors[246];
      }
    }

    return finalColors;
  }, [selectedColors]); // memoize the url

  var memoedUrl = useMemo(function () {
    var result = computeExteriorUrl(apiKey, client, colorMtd, community, directoryName, elevation, engineUrl, plan, selectedScheme, colorSelections, maxWidth, maxWidth > 0, undefined, undefined, descriptionPoints);
    return result;
  }, [apiKey, client, colorMtd, community, directoryName, elevation, engineUrl, plan, selectedScheme, colorSelections, maxWidth]);
  var prevUrl = usePrevious(memoedUrl);
  useEffect(function () {
    if (memoedUrl !== prevUrl) {
      startCallback();
    }

    if (!prevExteriorImg && exteriorImg && exteriorImg.src) {
      doneCallback();
    } else if (prevExteriorImg && exteriorImg && prevExteriorImg.src !== exteriorImg.src) {
      doneCallback();
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [memoedUrl, prevUrl, prevExteriorImg, exteriorImg]);
  useImageFetch(memoedUrl, setExteriorImg, mountedRef);
  useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  });

  var onClick = function onClick(e) {
    if (toggleExteriorMenu) {
      e.stopPropagation();
      toggleExteriorMenu();
    }
  }; // r e n d e r


  var src = exteriorImg ? exteriorImg.src : prevExteriorImg ? prevExteriorImg.src : null;
  var rootStyle = {
    width: '100%',
    height: '100%'
  };

  if (width && height) {
    rootStyle = {
      width: width,
      height: height
    };
  }

  return /*#__PURE__*/React.createElement("div", {
    style: rootStyle,
    onClick: onClick,
    ref: parentRef
  }, /*#__PURE__*/React.createElement(Snackbar, {
    severity: "info",
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    autoHideDuration: 3500,
    open: displayPaintedBrickInfoMsg,
    onClose: function onClose() {
      setDisplayPaintedBrickMessage(false);
    }
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "You must select a 'Painted Brick' option from the 'Brick' material to display this selection")), /*#__PURE__*/React.createElement(ImageDisplay, {
    src: src,
    style: style,
    flip: flip
  }));
};

ExteriorImage.propTypes = {
  flip: PropTypes.bool,
  toggleExteriorMenu: PropTypes.func,
  width: PropTypes.number,
  height: PropTypes.number,
  engineUrl: PropTypes.string,
  selectedScheme: SchemePropType,
  colorSelections: PropTypes.objectOf(ColorPropType),
  client: PropTypes.string.isRequired,
  directoryName: PropTypes.string.isRequired,
  community: PropTypes.string.isRequired,
  plan: PropTypes.string.isRequired,
  elevation: ElevationPropType.isRequired,
  colorMtd: PropTypes.string.isRequired,
  style: PropTypes.object,
  doneCallback: PropTypes.func,
  startCallback: PropTypes.func,
  maxWidth: PropTypes.number,
  descriptionPoints: PropTypes.arrayOf({
    materialId: PropTypes.number.isRequired,
    pointNumber: PropTypes.number.isRequired
  })
};
ExteriorImage.defaultProps = {
  startCallback: function startCallback() {},
  doneCallback: function doneCallback() {},
  toggleExteriorMenu: undefined,
  style: {},
  width: 0,
  height: 0,
  maxWidth: 0,
  engineUrl: 'https://hemi.anewgo.com/api/exterior',
  colorSelections: {},
  selectedScheme: null,
  flip: false
};
export default ExteriorImage;